import { useRef, useState } from 'react';
import { District } from '../../types/schema';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { authClient } from '../../utils/amplifyClient';
import { updateLocation } from '../../graphql/mutations';

interface AddDistrictModalProps {
    regionId: string;
    regionName: string;
    district?: District;
    onClose: () => void;
    onSubmit: (name: string, description: string, regionalDivisionId: string) => void;
}

export const AddDistrictModal = ({ regionId, regionName, district, onClose, onSubmit }: AddDistrictModalProps) => {
    const { updateDistrict, refreshRegionAndLocation } = useRegionAndLocationContext();
    const [name, setName] = useState(district?.name || '');
    const [description, setDescription] = useState(district?.description || '');
    const [saving, setSaving] = useState(false);
    /** Only dismiss when press + release both land on the backdrop (not after drag-select from a field). */
    const dismissOnClickRef = useRef(false);

    const isEdit = !!district;

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSaving(true);
        try {
            if (isEdit) {
                await authClient.graphql({
                    query: updateLocation,
                    variables: { input: { id: district.id, name: name.trim(), description: description.trim() || null } },
                });
                updateDistrict(district.id, { name: name.trim(), description: description.trim() || null });
                await refreshRegionAndLocation();
                onClose();
            } else {
                await onSubmit(name.trim(), description.trim(), regionId);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal-overlay"
            onMouseDown={(e) => {
                dismissOnClickRef.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
                if (dismissOnClickRef.current && e.target === e.currentTarget) {
                    onClose();
                }
                dismissOnClickRef.current = false;
            }}
        >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEdit ? 'Edit District' : `Add District to ${regionName}`}</h2>
                    <button className="modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <div className="modal-field">
                        <label>District Name</label>
                        <input
                            type="text"
                            placeholder="e.g. Faskin Lease"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className="modal-field">
                        <label>District Description</label>
                        <textarea
                            placeholder="Optional description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <div className="modal-field">
                        <label>Parent Region</label>
                        <input type="text" value={regionName} disabled />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="modal-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="modal-btn-primary" onClick={handleSubmit} disabled={!name.trim() || saving}>
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create District'}
                    </button>
                </div>
            </div>
        </div>
    );
};
